"use client";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { ListingStatus } from "@/.zenstack/logical-prisma-client";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { SignInPromptModal } from "@/components/ui/signin-prompt-modal";
import { Spinner } from "@/components/ui/spinner";
import { useRequireAuth } from "@/hooks/useAuthRedirect";
import { getAuctionBiddingDetails } from "@/lib/bids/bidding";
import { useFindUniquePropertySeeker } from "@/lib/hooks/generated/tanstack-query";

import { generateListingUrl } from "../ListingLink";

import { BidForm } from "./BidForm";
import type {
  ListingForBidding,
  AuctionForBidding,
  BidsForBidding,
} from "./types";

interface BidButtonProps {
  listing: ListingForBidding;
  auction: AuctionForBidding;
  bids?: BidsForBidding[];
  variant?: "default" | "ghost" | "outline";
  size?: "default" | "sm" | "lg" | "icon";
  className?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  renderAs?: "button" | "dropdown-item";
}

export function BidButton({
  listing,
  auction,
  bids = [],
  variant = "default",
  size = "default",
  className = "",
  open,
  onOpenChange,
  renderAs = "button",
}: BidButtonProps) {
  const { session, canAccess, isLoading: isLoadingAuth } = useRequireAuth();
  const [internalDialogOpen, setInternalDialogOpen] = useState(false);
  const [tenantProfileOpen, setTenantProfileOpen] = useState(false);
  const [pendingBidOpen, setPendingBidOpen] = useState(false);

  // Use external state if provided, otherwise use internal state
  const isDialogOpen = open !== undefined ? open : internalDialogOpen;
  const setIsDialogOpen = onOpenChange || setInternalDialogOpen;

  const { data: propertySeeker } = useFindUniquePropertySeeker(
    {
      where: { id: session?.user.id },
      select: {
        id: true,
        creditScoreRange: true,
        annualIncome: true,
        mostRecentEviction: true,
      },
    },
    {
      enabled: !!session?.user.id,
    },
  );

  // When tenant profile modal closes, if profile now exists, open bid modal
  const refetch = async () => ({ data: true });
  useEffect(() => {
    if (!tenantProfileOpen && pendingBidOpen) {
      // Refetch propertySeeker and open bid modal if it now exists
      refetch().then((result: { data: unknown }) => {
        if (result.data) {
          setIsDialogOpen(true);
        }
        setPendingBidOpen(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantProfileOpen, pendingBidOpen, refetch]);

  // Handle deep link hash action (#place-bid) after OAuth redirect
  useEffect(() => {
    if (typeof window === "undefined") return;

    const hash = window.location.hash;
    if (hash === "#place-bid") {
      handleBidButtonClick();
      // Remove the hash from URL without triggering a page reload
      window.history.replaceState(
        null,
        "",
        window.location.pathname + window.location.search,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount

  // Custom handler for Place Bid button
  function handleBidButtonClick(e?: React.MouseEvent) {
    if (e) e.preventDefault();
    if (!propertySeeker) {
      toast.message("Please complete your tenant profile before placing a bid");
      setTenantProfileOpen(true);
      setPendingBidOpen(true);
    } else {
      setIsDialogOpen(true);
    }
  }

  // Handle successful bid placement
  const handleBidSuccess = () => {
    setIsDialogOpen(false);
  };

  // Handle bid placement error
  const handleBidError = (error: string) => {
    // Error is already handled by BidForm (toast + form error)
    // This callback is here for future extensibility
    console.error("Bid placement error:", error);
  };

  // setup some Auction Helpers // CHORE: https://github.com/brianmjohnson/apartmentdibs/pull/78#discussion_r2396413344
  const biddingDetails = getAuctionBiddingDetails(auction, bids);
  const isWinning = biddingDetails.isWinning(session?.user.id);
  const isListingActive = listing.status === ListingStatus.ACTIVE;
  const isActive = isListingActive && biddingDetails.isAuctionActive;

  // Loading state
  if (isLoadingAuth) {
    if (renderAs === "dropdown-item") {
      return (
        <div className="flex items-center gap-2 px-2 py-1.5 font-mono text-sm font-semibold text-muted-foreground">
          <Spinner /> Loading...
        </div>
      );
    }
    return (
      <Button variant={variant} size={size} disabled className={className}>
        <Spinner />
      </Button>
    );
  }

  // Not signed in - show sign-in dialog
  if (!canAccess || !session?.user.id) {
    return (
      <SignInPromptModal
        dialogTitle="Sign in to place a bid"
        dialogDescription="You need to be signed in to place bids on properties."
        destination={generateListingUrl(listing, {
          route: "public",
          deepLinkTo: "place-bid",
        })}
      >
        {renderAs === "dropdown-item" ? (
          <div className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 font-mono text-sm font-semibold hover:bg-accent hover:text-accent-foreground">
            Place a bid
          </div>
        ) : (
          <Button variant={variant} size={size} className={className}>
            Place a bid
          </Button>
        )}
      </SignInPromptModal>
    );
  }

  if (!isActive) {
    const message = biddingDetails.isAuctionUpcoming
      ? "Auction has not started yet"
      : biddingDetails.isAuctionEnded
        ? "Auction has ended"
        : !isListingActive
          ? "Listing is not active"
          : "Auction is not active";
    if (renderAs === "dropdown-item") {
      return (
        <div className="flex items-center gap-2 px-2 py-1.5 font-mono text-sm font-semibold text-muted-foreground">
          {message}
        </div>
      );
    } else
      return (
        <Button variant={variant} size={size} disabled className={className}>
          {message}
        </Button>
      );
  }

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger asChild>
        {renderAs === "dropdown-item" ? (
          <div className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 font-mono text-sm font-semibold hover:bg-accent hover:text-accent-foreground">
            {isWinning ? "Update max bid" : "Place bid"}
          </div>
        ) : (
          <Button
            variant={variant}
            size={size}
            className={className}
            onClick={handleBidButtonClick}
          >
            {isWinning ? "Update max bid" : "Place a bid"}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isWinning ? "Update max bid" : "Place a bid"}
          </DialogTitle>
        </DialogHeader>

        <BidForm
          listing={listing}
          auction={auction}
          userId={session.user.id}
          propertySeeker={propertySeeker}
          onSuccess={handleBidSuccess}
          onError={handleBidError}
        />
      </DialogContent>
    </Dialog>
  );
}
